import * as React from 'react'
import {
    Box,
    MenuItem,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import RestaurantRoundedIcon from '@mui/icons-material/RestaurantRounded'

import useAutoTranslate from '../hooks/useAutoTranslate'

const PLUGIN_KEY = 'meadrep'
const DEFAULT_CHECK_INTERVAL_MINUTES = '10'
const DEFAULT_MINIMUM_FOOD = '100000'
const DEFAULT_MAIN_FOOD_STORAGE = '700000'
const DEFAULT_MINIMUM_SEND_AMOUNT = '100000'
const DEFAULT_TIME_SKIP_SEND_TYPE = '30'
const DEFAULT_TIME_SKIP_SEND_AMOUNT = '2'
const DEFAULT_ENABLE_FOOD_WARNINGS = true
const DEFAULT_FOOD_WARNING_THRESHOLD = '300000'

const SKIP_TIME_OPTIONS = Object.freeze([
    { value: '1', label: '1 Minute' },
    { value: '5', label: '5 Minutes' },
    { value: '10', label: '10 Minutes' },
    { value: '30', label: '30 Minutes' },
    { value: '60', label: '60 Minutes' },
    { value: '300', label: '5 Hours' },
    { value: '1440', label: '24 Hours' }
])

const cloneValue = value =>
    typeof structuredClone === 'function' ?
        structuredClone(value) :
        JSON.parse(JSON.stringify(value ?? {}))

const normalizePositiveNumberText = (value, fallback = '') => {
    if ([undefined, null, ''].includes(value))
        return fallback

    const parsed = Number.parseInt(String(value), 10)
    return Number.isFinite(parsed) && parsed >= 0 ? String(parsed) : fallback
}

const normalizeSkipType = value =>
    SKIP_TIME_OPTIONS.some(option => String(option.value) === String(value)) ?
        String(value) :
        DEFAULT_TIME_SKIP_SEND_TYPE

const FIELD_INPUT_SX = {
    '& .MuiOutlinedInput-root': {
        minHeight: 42,
        borderRadius: '10px',
        backgroundColor: 'rgba(24, 36, 57, 0.76)'
    },
    '& .MuiInputBase-input': {
        fontSize: '1.01rem',
        fontWeight: 700,
        color: '#f5f7ff'
    }
}

function Helper({ children }) {
    return (
        <Stack direction="row" spacing={0.7} alignItems="center" sx={{ mt: 0.8 }}>
            <InfoOutlinedIcon sx={{ fontSize: 13, color: '#95a5c4' }} />
            <Typography sx={{ fontSize: '0.89rem', color: '#95a5c4', lineHeight: 1.35 }}>{children}</Typography>
        </Stack>
    )
}

function FieldTitle({ children }) {
    return (
        <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>
            {children}
        </Typography>
    )
}

function ToggleField({ checked, onChange, label, helper }) {
    return (
        <Box sx={{ minHeight: 54 }}>
            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ pt: 1.15 }}>
                <Switch
                    checked={checked}
                    onChange={event => onChange(event.target.checked)}
                    sx={{
                        width: 48,
                        height: 29,
                        p: 0,
                        '& .MuiSwitch-switchBase': {
                            p: '2.5px',
                            '&.Mui-checked': {
                                transform: 'translateX(18px)',
                                color: '#ffffff'
                            },
                            '&.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#2f7cff',
                                opacity: 1
                            }
                        },
                        '& .MuiSwitch-thumb': {
                            width: 24,
                            height: 24,
                            boxShadow: 'none'
                        },
                        '& .MuiSwitch-track': {
                            opacity: 1,
                            borderRadius: '999px',
                            backgroundColor: 'rgba(111, 123, 143, 0.9)'
                        }
                    }}
                />
                <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800 }}>
                    {label}
                </Typography>
            </Stack>
            <Helper>{helper}</Helper>
        </Box>
    )
}

function SectionHeading({ children }) {
    return (
        <Stack direction="row" alignItems="center" spacing={1.3} sx={{ mb: 2.2 }}>
            <Typography sx={{ color: '#f5f7ff', fontSize: '1.05rem', fontWeight: 800 }}>{children}</Typography>
            <Box sx={{ flex: 1, height: 1, backgroundColor: 'rgba(129, 146, 178, 0.22)' }} />
        </Stack>
    )
}

export default function FoodCheckSettingsPanel({ userPlugins, onChange, appLanguage = 'en' }) {
    const pluginState = React.useMemo(
        () => userPlugins?.[PLUGIN_KEY] && typeof userPlugins[PLUGIN_KEY] === 'object' ? userPlugins[PLUGIN_KEY] : {},
        [userPlugins]
    )

    const t = useAutoTranslate(appLanguage, [
        'Food Check Settings',
        'Configure automatic food monitoring and management',
        'Check Interval (Minutes)',
        'How often to check food levels in minutes',
        'Minimum Food',
        'Minimum amount of food to keep',
        'Main Food Storage',
        'Main food storage capacity',
        'Minimum Send Amount',
        'Minimum amount of food to send',
        'Time Skip Send Type',
        'Type of time skip to use when sending food',
        'Time Skip Send Amount',
        'Amount of time skips to use when sending food NOTE: Make sure you have the research in your research tower to lower the send time!',
        'Food Discord Warning',
        'Enable Food Warnings',
        'Enable warnings when food is low',
        'Food Warning Threshold',
        'Food amount that triggers warnings. To enable the Warning you need to configure the Discord Webhook in \'Discord Notifications\'',
        ...SKIP_TIME_OPTIONS.map(option => option.label)
    ])

    const config = React.useMemo(() => ({
        checkIntervalMinutes: normalizePositiveNumberText(pluginState?.checkIntervalMinutes, DEFAULT_CHECK_INTERVAL_MINUTES),
        minimumFood: normalizePositiveNumberText(pluginState?.minimumFood, DEFAULT_MINIMUM_FOOD),
        mainFoodStorage: normalizePositiveNumberText(pluginState?.mainFoodStorage, DEFAULT_MAIN_FOOD_STORAGE),
        minimumSendAmount: normalizePositiveNumberText(pluginState?.minimumSendAmount, DEFAULT_MINIMUM_SEND_AMOUNT),
        timeSkipSendType: normalizeSkipType(pluginState?.timeSkipSendType),
        timeSkipSendAmount: normalizePositiveNumberText(pluginState?.timeSkipSendAmount, DEFAULT_TIME_SKIP_SEND_AMOUNT),
        enableFoodWarnings: typeof pluginState?.enableFoodWarnings === 'boolean' ? pluginState.enableFoodWarnings : DEFAULT_ENABLE_FOOD_WARNINGS,
        foodWarningThreshold: normalizePositiveNumberText(pluginState?.foodWarningThreshold, DEFAULT_FOOD_WARNING_THRESHOLD)
    }), [
        pluginState?.checkIntervalMinutes,
        pluginState?.minimumFood,
        pluginState?.mainFoodStorage,
        pluginState?.minimumSendAmount,
        pluginState?.timeSkipSendType,
        pluginState?.timeSkipSendAmount,
        pluginState?.enableFoodWarnings,
        pluginState?.foodWarningThreshold
    ])

    const commit = React.useCallback(nextConfig => {
        const nextPlugins = cloneValue(userPlugins ?? {})
        nextPlugins[PLUGIN_KEY] ??= {}
        nextPlugins[PLUGIN_KEY].checkIntervalMinutes = normalizePositiveNumberText(nextConfig.checkIntervalMinutes, DEFAULT_CHECK_INTERVAL_MINUTES)
        nextPlugins[PLUGIN_KEY].minimumFood = normalizePositiveNumberText(nextConfig.minimumFood, DEFAULT_MINIMUM_FOOD)
        nextPlugins[PLUGIN_KEY].mainFoodStorage = normalizePositiveNumberText(nextConfig.mainFoodStorage, DEFAULT_MAIN_FOOD_STORAGE)
        nextPlugins[PLUGIN_KEY].minimumSendAmount = normalizePositiveNumberText(nextConfig.minimumSendAmount, DEFAULT_MINIMUM_SEND_AMOUNT)
        nextPlugins[PLUGIN_KEY].timeSkipSendType = normalizeSkipType(nextConfig.timeSkipSendType)
        nextPlugins[PLUGIN_KEY].timeSkipSendAmount = normalizePositiveNumberText(nextConfig.timeSkipSendAmount, DEFAULT_TIME_SKIP_SEND_AMOUNT)
        nextPlugins[PLUGIN_KEY].enableFoodWarnings = Boolean(nextConfig.enableFoodWarnings)
        nextPlugins[PLUGIN_KEY].foodWarningThreshold = normalizePositiveNumberText(nextConfig.foodWarningThreshold, DEFAULT_FOOD_WARNING_THRESHOLD)
        onChange(nextPlugins)
    }, [onChange, userPlugins])

    const update = updater => {
        const nextConfig = cloneValue(config)
        updater(nextConfig)
        commit(nextConfig)
    }

    return (
        <Paper
            sx={{
                p: { xs: 2.2, md: 3.1 },
                borderRadius: '22px',
                border: '1px solid rgba(129, 146, 178, 0.18)',
                background: 'linear-gradient(180deg, rgba(27, 40, 63, 0.9) 0%, rgba(24, 35, 56, 0.86) 100%)',
                boxShadow: '0 22px 46px rgba(7, 13, 26, 0.22)'
            }}
        >
            <Stack spacing={3.2}>
                <Box
                    sx={{
                        p: { xs: 2, md: 2.5 },
                        borderRadius: '18px',
                        border: '1px solid rgba(129, 146, 178, 0.14)',
                        backgroundColor: 'rgba(20, 31, 52, 0.5)'
                    }}
                >
                    <Stack spacing={3}>
                        <Box>
                            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 0.55 }}>
                                <RestaurantRoundedIcon sx={{ color: '#f5f7ff', fontSize: 22 }} />
                                <Typography sx={{ color: '#f5f7ff', fontSize: '1.42rem', fontWeight: 800 }}>
                                    {t('Food Check Settings')}
                                </Typography>
                            </Stack>
                            <Typography sx={{ color: '#9fb0cc', fontSize: '1.02rem' }}>
                                {t('Configure automatic food monitoring and management')}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }, gap: 2.4 }}>
                            <Box>
                                <FieldTitle>{t('Check Interval (Minutes)')}</FieldTitle>
                                <TextField
                                    fullWidth
                                    value={config.checkIntervalMinutes}
                                    onChange={event => update(next => { next.checkIntervalMinutes = event.target.value })}
                                    sx={FIELD_INPUT_SX}
                                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                />
                                <Helper>{t('How often to check food levels in minutes')}</Helper>
                            </Box>

                            <Box>
                                <FieldTitle>{t('Minimum Food')}</FieldTitle>
                                <TextField
                                    fullWidth
                                    value={config.minimumFood}
                                    onChange={event => update(next => { next.minimumFood = event.target.value })}
                                    sx={FIELD_INPUT_SX}
                                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                />
                                <Helper>{t('Minimum amount of food to keep')}</Helper>
                            </Box>

                            <Box>
                                <FieldTitle>{t('Main Food Storage')}</FieldTitle>
                                <TextField
                                    fullWidth
                                    value={config.mainFoodStorage}
                                    onChange={event => update(next => { next.mainFoodStorage = event.target.value })}
                                    sx={FIELD_INPUT_SX}
                                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                />
                                <Helper>{t('Main food storage capacity')}</Helper>
                            </Box>

                            <Box>
                                <FieldTitle>{t('Minimum Send Amount')}</FieldTitle>
                                <TextField
                                    fullWidth
                                    value={config.minimumSendAmount}
                                    onChange={event => update(next => { next.minimumSendAmount = event.target.value })}
                                    sx={FIELD_INPUT_SX}
                                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                />
                                <Helper>{t('Minimum amount of food to send')}</Helper>
                            </Box>

                            <Box>
                                <FieldTitle>{t('Time Skip Send Type')}</FieldTitle>
                                <TextField
                                    select
                                    fullWidth
                                    value={config.timeSkipSendType}
                                    onChange={event => update(next => { next.timeSkipSendType = event.target.value })}
                                    sx={FIELD_INPUT_SX}
                                >
                                    {SKIP_TIME_OPTIONS.map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {t(option.label)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <Helper>{t('Type of time skip to use when sending food')}</Helper>
                            </Box>

                            <Box>
                                <FieldTitle>{t('Time Skip Send Amount')}</FieldTitle>
                                <TextField
                                    fullWidth
                                    value={config.timeSkipSendAmount}
                                    onChange={event => update(next => { next.timeSkipSendAmount = event.target.value })}
                                    sx={FIELD_INPUT_SX}
                                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                />
                                <Helper>{t('Amount of time skips to use when sending food NOTE: Make sure you have the research in your research tower to lower the send time!')}</Helper>
                            </Box>
                        </Box>

                        <Box>
                            <SectionHeading>{t('Food Discord Warning')}</SectionHeading>

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }, gap: 2.4 }}>
                                <ToggleField
                                    checked={config.enableFoodWarnings}
                                    onChange={value => update(next => { next.enableFoodWarnings = value })}
                                    label={t('Enable Food Warnings')}
                                    helper={t('Enable warnings when food is low')}
                                />

                                <Box>
                                    <FieldTitle>{t('Food Warning Threshold')}</FieldTitle>
                                    <TextField
                                        fullWidth
                                        value={config.foodWarningThreshold}
                                        onChange={event => update(next => { next.foodWarningThreshold = event.target.value })}
                                        sx={FIELD_INPUT_SX}
                                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                    />
                                    <Helper>{t('Food amount that triggers warnings. To enable the Warning you need to configure the Discord Webhook in \'Discord Notifications\'')}</Helper>
                                </Box>
                            </Box>
                        </Box>
                    </Stack>
                </Box>
            </Stack>
        </Paper>
    )
}
